import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDayUi } from './dayUiStore';
import styles from './GestureHelp.module.css';

/** Shortcut texts show Cmd on Apple platforms and Ctrl everywhere else (ui-redesign §3.6.3). */
export function primaryModifierLabel(): string {
  const platform = navigator.platform;
  return /mac|iphone|ipad|ipod/i.test(platform) ? 'Cmd' : 'Ctrl';
}

/** Option on Apple platforms, Alt elsewhere. */
export function altModifierLabel(): string {
  return /mac|iphone|ipad|ipod/i.test(navigator.platform) ? 'Option' : 'Alt';
}

/** The help table's groups and their rows, in order (2026-10b §10.2). */
const GROUP_ROWS = {
  gestures: [
    'clickEmpty',
    'dragEmpty',
    'overlay',
    'moveBlock',
    'resizeBlock',
    'esc',
    'clickBlock',
    'keyboardMove',
    'keyboardOverlay',
    'undo',
  ],
  quickMark: ['quickOnce', 'quickBeside', 'quickLock', 'quickDigit', 'quickDrag'],
  blockEdit: ['contextMenu', 'editText', 'changeProject'],
  dateView: ['periodStep', 'today', 'switchView', 'switchChord'],
  monthGrid: ['monthMove', 'monthRow', 'monthPage', 'monthOpen'],
} as const;

type GroupKey = keyof typeof GROUP_ROWS;
const GROUP_KEYS = Object.keys(GROUP_ROWS) as GroupKey[];
/** Groups that end with the input-method note. */
const IME_NOTE_GROUP: GroupKey = 'dateView';

/**
 * Read-only gesture table (ui-redesign §3.6.3), opened by the top bar's "?" button or the `?`/`？` key.
 * Mounted once by the shell, so it works in the day, week and month views (2026-10c §8).
 * Rendered to `document.body`; Esc and the backdrop close it.
 */
export function GestureHelp() {
  const { t } = useTranslation();
  const open = useDayUi((state) => state.helpOpen);
  const closeHelp = useDayUi((state) => state.closeHelp);
  const mod = primaryModifierLabel();
  const keys = { mod, ctrl: 'Ctrl', alt: altModifierLabel() };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeHelp();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, closeHelp]);

  if (!open) return null;
  return createPortal(
    <div
      className={styles.backdrop}
      data-testid="gesture-help"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) closeHelp();
      }}
    >
      <div className={styles.panel} role="dialog" aria-modal="true" aria-label={t('help.title')}>
        <header className={styles.header}>
          <h2 className={styles.title}>{t('help.title')}</h2>
          <button type="button" className={styles.close} aria-label={t('help.close')} onClick={closeHelp}>
            ×
          </button>
        </header>
        <table className={styles.table}>
          {GROUP_KEYS.map((group) => (
            <tbody key={group} data-testid={`help-group-${group}`}>
              <tr>
                <th scope="colgroup" colSpan={2} className={styles.groupTitle}>
                  {t(`help.group.${group}`)}
                </th>
              </tr>
              {GROUP_ROWS[group].map((key) => (
                <tr key={key} data-testid="help-row">
                  <th scope="row">{t(`help.row.${key}`, keys)}</th>
                  <td>{t(`help.row.${key}Desc`, keys)}</td>
                </tr>
              ))}
              {group === IME_NOTE_GROUP ? (
                <tr>
                  <td colSpan={2} className={styles.note} data-testid="help-ime-note">
                    {t('help.imeNote', keys)}
                  </td>
                </tr>
              ) : null}
            </tbody>
          ))}
        </table>
      </div>
    </div>,
    document.body,
  );
}
